import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { moduleSubscriptionGate, enterpriseAdminGate, hrGate } from './layout/menuTree';

// İşe hazırlık kilidindeyken erişilebilir kalan yollar. Çıkış bir route değil
// (Header aksiyonu), her durumda çalışır.
const PREBOARDING_WHITELIST = ['/my-preboarding', '/profile'];

export default function ProtectedRoute({ component: Component, privileges = [], moduleGated = false, module = undefined, requiresEnterpriseAdmin = false, requiresHr = false, ...rest }) {
  const { user, loading, isSuperAdmin, isEnterpriseAdmin, isHr, canManageTenants, isPreboardingLocked, hasModule, hasSeat } = useAuth();

  if (loading) {
    return null;
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!user) {
          const redirectPath = props.location.pathname + props.location.search;
          return <Redirect to={`/login?redirect=${encodeURIComponent(redirectPath)}`} />;
        }

        if (isPreboardingLocked) {
          const allowed = PREBOARDING_WHITELIST.some(
            (p) => props.location.pathname === p || props.location.pathname.startsWith(p + '/'),
          );
          if (!allowed) {
            return <Redirect to="/my-preboarding" />;
          }
        }

        // Task 16 (HR Analytics Plan 2) — moduleGated routes require the tenant to
        // actually have the module subscribed, independent of the isSuperAdmin/
        // isEnterpriseAdmin bypass below. Before this, a route with `privileges`
        // but no `moduleGated` let an enterpriseAdmin on an unsubscribed tenant
        // reach the page by typing the URL directly — SidebarMenu.js already closed
        // this gap at the menu level (moduleGateCode + hasModule, see hasAccess),
        // this mirrors the EXACT same primitive at the route level rather than
        // inventing a second one. moduleGateCode prefers the explicit `module`
        // prop (required for self-service routes whose privileges are [] —
        // inference from privileges[0] silently yields undefined there and the
        // gate would never apply; same class of bug as menuTree's ledger T9),
        // else infers the code from privileges[0] (e.g. 'HA.Read' → 'HA') —
        // the HA routes still rely on the inference path.
        //
        // Modül Bazlı Lisanslama, düzeltme turu 4 (İ-8) — KOLTUK KAPISININ ROTA İKİZİ.
        // Kapı buraya kadar YALNIZ abonelik soruyordu: ölçümlü bir modülde koltuğu
        // olmayan kişi (ya da joker ayrıcalıklı koltuksuz enterpriseAdmin) menüde
        // öğeyi görmese de URL'yi yazarak / yer imiyle / derin bağlantıyla sayfayı
        // açıyor, sayfa yükleniyor ve uçlar 403 `license.seatUnavailable` dönüyordu
        // (kırmızı toast + 10 dakikada bir refresh-session turu) — Ruling 64'ün
        // önlemeye çalıştığı hata sınıfının tam rota tarafı. Menünün KENDİ saf
        // fonksiyonu kullanılır (menuTree.moduleSubscriptionGate = hasModule VE
        // hasSeat; ikinci bir mekanizma İCAT EDİLMEZ — yukarıdaki enterpriseAdminGate
        // ile AYNI disiplin). DEĞİŞMEZ 0: `hasSeat` ölçümsüz ve varsayılan modüllerde
        // VE `seats` alanı olmayan eski oturumlarda HER ZAMAN true döner
        // (resolveHasSeat), yani ölçümsüz kiracıda davranış DEĞİŞMEZ.
        if (!moduleSubscriptionGate({ moduleGated, module, privileges }, { hasModule, hasSeat })) {
          return <Redirect to="/" />;
        }

        // B2 partisi (PMB-08 kuyruğu) — MENÜ ile ROTA aynı kapıyı taşımalı.
        // MenuConfig'te `requiresEnterpriseAdmin: true` taşıyan iki uç
        // (/performance/pips, /performance/objections) rota tarafında hâlâ
        // yalnız `PM.Read` istiyordu: menü onları gizlerken URL'yi elle yazan
        // PM.Read'li kullanıcı sayfayı açabiliyordu. Sunucu boş liste döndüğü
        // için SIZINTI yok — ama kullanıcı kalıcı olarak boş bir ekranda
        // kalıyordu ("kapı doğru, rota yalancı" — menü tarafında kapatılan
        // hatanın ikizi). Menünün kullandığı `enterpriseAdminGate` primitifi
        // AYNEN kullanılır (moduleSubscriptionGate'in burada yeniden kullanılmasıyla
        // AYNI disiplin — ikinci bir mekanizma İCAT EDİLMEZ); superAdmin
        // muafiyeti de o primitifin içinde.
        if (!enterpriseAdminGate({ requiresEnterpriseAdmin }, { isEnterpriseAdmin, isSuperAdmin })) {
          return <Redirect to="/" />;
        }

        // İK rolü ayrımı — `requiresHr` rotaları menüdeki aynı bayrağın ikizi (menuTree.hrGate).
        // İK artık Şirket Yöneticisi demek değil: İK rolü kullanan kiracıda genel müdür bu
        // yüzeylere girmez, İK rolü sahibi (yönetici olmasa da) girer.
        if (!hrGate({ requiresHr }, { isHr, isSuperAdmin })) {
          return <Redirect to="/" />;
        }

        if (privileges.length > 0) {
          // 'tenant.manage' is a virtual privilege — superAdmin OR any user
          // with a multi-tenant scope (holding admin). Don't fall through to
          // isEnterpriseAdmin, which would let plain subsidiary admins in.
          const isTenantManageGate = privileges.includes('tenant.manage');
          const hasAccess = isTenantManageGate
            ? canManageTenants
            : (
              isSuperAdmin ||
              isEnterpriseAdmin ||
              privileges.some((priv) => user.privileges?.includes(priv))
            );

          if (!hasAccess) {
            return <Redirect to="/" />;
          }
        }

        return <Component {...props} />;
      }}
    />
  );
}
